import { useEffect, useRef } from "react";

/**
 * Slow drifting ambient particles behind the book stage.
 * Canvas-based, GPU cheap, respects prefers-reduced-motion.
 */
export default function LightParticles({ count = 35 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = canvas.getContext("2d");
    let raf = 0;
    let width = 0;
    let height = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const rand = (a, b) => a + Math.random() * (b - a);
    const particles = Array.from({ length: count }).map(() => ({
      x: rand(0, width),
      y: rand(0, height),
      r: rand(0.6, 2.4),
      vx: rand(-0.05, 0.15),
      vy: rand(-0.2, -0.05),
      a: rand(0.04, 0.12),
      hue: Math.random() < 0.5 ? "201, 169, 97" : "245, 241, 232",
      tw: rand(0.001, 0.004),
      ph: rand(0, Math.PI * 2),
    }));

    const tick = (t) => {
      ctx.clearRect(0, 0, width, height);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -10) { p.y = height + 10; p.x = rand(0, width); }
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        const flicker = 0.6 + 0.4 * Math.sin(t * p.tw + p.ph);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.hue}, ${p.a * flicker})`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = `rgba(${p.hue}, ${p.a * flicker * 0.6})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
      data-testid="book-particles"
    />
  );
}
